import {
  REVISION_DETAIL_FIELD_SECTIONS,
  createRevisionDetailDraft,
} from "./revisionRapidaSaludDetailUtils";

const field = (id, label, section, name, options = {}) => ({
  id,
  label,
  kind: "field",
  section,
  name,
  maxLength: options.maxLength,
  type: options.type || "text",
  multiline: Boolean(options.multiline),
  defaultVisible: Boolean(options.defaultVisible),
  size: options.size || 150,
  projectionKey: options.projectionKey || id,
});

const slot = (id, label, key, index, options = {}) => ({
  id,
  label,
  kind: "slot",
  key,
  index,
  maxLength: 255,
  defaultVisible: Boolean(options.defaultVisible),
  size: options.size || 210,
});

export const REVISION_EXCEL_EDITABLE_COLUMNS = Object.freeze([
  field("codigo", "Código", "identity", "codigoBeneficiario", { defaultVisible: true, maxLength: 55, size: 125 }),
  field("nombre", "Nombre", "identity", "nombre", { defaultVisible: true, maxLength: 45, size: 150 }),
  field("apellido", "Apellido", "identity", "apellido", { defaultVisible: true, maxLength: 45, size: 150 }),
  field("cedula", "Cédula", "identity", "cedula", { maxLength: 45, size: 130 }),
  field("telefono", "Teléfono", "contact", "telefono", { maxLength: 45, size: 145 }),
  field("edad", "Edad", "demographics", "edad", { defaultVisible: true, maxLength: 255, size: 120 }),
  field("sexo", "Sexo", "demographics", "sexo", { defaultVisible: true, maxLength: 45, size: 105 }),
  field("genero", "Género", "demographics", "genero", { maxLength: 255 }),
  field("fechaNacimiento", "Fecha nacimiento", "demographics", "fechaNacimiento", { type: "date", size: 155 }),
  field("rangoEdad", "Rango edad", "demographics", "rangoEdad", { maxLength: 45, size: 125 }),
  field("lgbtqi", "LGBTIQ+", "demographics", "lgbtqi", { maxLength: 45, size: 115 }),
  field("ingresos", "Ingresos", "demographics", "ingresos", { maxLength: 45, size: 115 }),
  field("cabezaFamilia", "Cabeza de familia", "demographics", "cabezaFamilia", { maxLength: 45, size: 155 }),
  field("comunidad", "Comunidad", "location", "comunidad", { defaultVisible: true, maxLength: 45, size: 185 }),
  field("peso", "Peso", "evaluation", "peso", { maxLength: 45, size: 100 }),
  field("comentarios", "Comentarios", "clinical", "comentarios", { maxLength: 45, multiline: true, size: 240 }),
  slot("motivo1", "Motivo 1", "consultationReasons", 0),
  slot("motivo2", "Motivo 2", "consultationReasons", 1),
  slot("motivo3", "Motivo 3", "consultationReasons", 2),
  slot("motivo4", "Motivo 4", "consultationReasons", 3),
  slot("motivo5", "Motivo 5", "consultationReasons", 4),
  slot("diagnostico1", "Diagnóstico 1", "diagnoses", 0),
  slot("diagnostico2", "Diagnóstico 2", "diagnoses", 1),
  slot("diagnostico3", "Diagnóstico 3", "diagnoses", 2),
  slot("diagnostico4", "Diagnóstico 4", "diagnoses", 3),
  slot("diagnostico5", "Diagnóstico 5", "diagnoses", 4),
]);

export const REVISION_EXCEL_COLUMN_BY_ID = Object.freeze(
  Object.fromEntries(REVISION_EXCEL_EDITABLE_COLUMNS.map((column) => [column.id, column]))
);

export const REVISION_PROTECTED_CONTEXT_FIELDS = Object.freeze([
  "location.estado",
  "location.municipio",
  "location.parroquia",
  "location.centro",
  "evaluation.fechaEvaluacion",
]);

export const getRevisionExcelDraftValue = (draft, config) => {
  if (!draft || !config) return "";
  if (config.kind === "slot") return draft[config.key]?.[config.index] ?? "";
  return draft.fields?.[config.section]?.[config.name] ?? "";
};

export const getRevisionExcelFieldErrorKey = (config) =>
  config.kind === "slot" ? `${config.key}.${config.index}` : `${config.section}.${config.name}`;

export const getRevisionExcelFieldDefinition = (config) => {
  if (!config || config.kind !== "field") return null;
  return REVISION_DETAIL_FIELD_SECTIONS
    .find((section) => section.key === config.section)
    ?.fields.find((definition) => definition.name === config.name) || config;
};

export const getRevisionExcelProjectionValue = (record, config) => {
  if (!record) return "";
  return record[config.projectionKey] ?? "";
};

export const buildRevisionExcelRow = (record, activeRecordId, draft, detail) => {
  const active = String(record.id) === String(activeRecordId) && draft;
  const row = { ...record };
  REVISION_EXCEL_EDITABLE_COLUMNS.forEach((config) => {
    row[config.id] = active
      ? getRevisionExcelDraftValue(draft, config)
      : getRevisionExcelProjectionValue(record, config);
  });
  row.tratamientos = active && detail?.treatments?.length
    ? detail.treatments.map((item) => item.description || `Medicamento #${item.medicationId}`).join(", ")
    : "Consultar en detalle completo";
  return row;
};

export const getRevisionExcelChangedColumns = (serverSnapshot, draft) => {
  if (!serverSnapshot || !draft) return [];
  const baseline = createRevisionDetailDraft(serverSnapshot);
  return REVISION_EXCEL_EDITABLE_COLUMNS.filter(
    (config) => JSON.stringify(getRevisionExcelDraftValue(baseline, config)) !==
      JSON.stringify(getRevisionExcelDraftValue(draft, config))
  ).map((config) => config.id);
};

export const createRevisionExcelColumnVisibility = () => ({
  id: false,
  diagnosticoResumen: true,
  medico: true,
  source: true,
  status: true,
  tratamientos: false,
  ...Object.fromEntries(
    REVISION_EXCEL_EDITABLE_COLUMNS.map((config) => [config.id, config.defaultVisible])
  ),
});

const normalizeContextText = (value) => {
  if (typeof value !== "string") return "";
  return value.trim();
};

const firstContextText = (...values) =>
  values.map(normalizeContextText).find(Boolean) || "";

const resolvedContextValue = (value, resolved) =>
  value || (resolved ? "Sin dato" : "—");

export const getRevisionJornadaContext = ({
  summary,
  navigationContext = {},
  detail,
} = {}) => {
  const convocatoria = summary?.convocatoria || {};
  const summaryLocation = convocatoria.location || convocatoria.ubicacion || {};
  const summaryEvaluation = convocatoria.evaluation || convocatoria.evaluacion || {};
  const navigationLocation = navigationContext.location || navigationContext.ubicacion || {};
  const navigationEvaluation = navigationContext.evaluation || navigationContext.evaluacion || {};
  const detailResolved = Boolean(detail?.fields);

  if (detailResolved) {
    const detailLocation = detail.fields?.location || {};
    const detailEvaluation = detail.fields?.evaluation || {};

    return {
      estado: resolvedContextValue(normalizeContextText(detailLocation.estado), true),
      municipio: resolvedContextValue(normalizeContextText(detailLocation.municipio), true),
      parroquia: resolvedContextValue(normalizeContextText(detailLocation.parroquia), true),
      establecimiento: resolvedContextValue(normalizeContextText(detailLocation.centro), true),
      fechaAtencion: resolvedContextValue(normalizeContextText(detailEvaluation.fechaEvaluacion), true),
      source: "detail",
    };
  }

  return {
    estado: resolvedContextValue(firstContextText(
      summaryLocation.estado,
      convocatoria.estadoGeografico,
      convocatoria.estadoNombre,
      navigationLocation.estado,
      navigationContext.estadoGeografico,
      navigationContext.estadoNombre
    ), false),
    municipio: resolvedContextValue(firstContextText(
      summaryLocation.municipio,
      convocatoria.municipioNombre,
      convocatoria.municipio,
      navigationLocation.municipio,
      navigationContext.municipioNombre,
      navigationContext.municipio
    ), false),
    parroquia: resolvedContextValue(firstContextText(
      summaryLocation.parroquia,
      convocatoria.parroquiaNombre,
      convocatoria.parroquia,
      navigationLocation.parroquia,
      navigationContext.parroquiaNombre,
      navigationContext.parroquia
    ), false),
    establecimiento: resolvedContextValue(firstContextText(
      summaryLocation.centro,
      convocatoria.establecimientoSalud,
      convocatoria.establecimiento,
      convocatoria.centro,
      navigationLocation.centro,
      navigationContext.establecimiento,
      navigationContext.centro
    ), false),
    fechaAtencion: resolvedContextValue(firstContextText(
      summaryEvaluation.fechaEvaluacion,
      convocatoria.fechaAtencion,
      convocatoria.fechaEvaluacion,
      navigationEvaluation.fechaEvaluacion,
      navigationContext.fechaAtencion
    ), false),
    source: "global",
  };
};

export const isRevisionExcelColumnEditable = (config, controller) => {
  if (!config || !controller?.legacy) return false;
  if (config.kind === "slot" && config.key === "diagnoses") return controller.diagnosesEditable;
  return controller.fieldsEditable;
};
